/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useMemo, useState
} from 'react';
import {
    providerConfigRegistry,
    setProviderParametersField
} from '../Common/providerConfigRegistry';
import AnatomylivePrompt from '../Common/AnatomyLivePrompt';
import {
    Separator
} from 'shared/components/ui/separator';
import {
    ArrowUpFromLine, WandSparkles, Globe, AlertCircle
} from 'lucide-react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Output
} from './Output';
import {
    unifiedModelWSService
} from 'shared/api/services/PROJECT-O/WEBSOCKETS/UnifiedModel.websocket.service';
import {
    Context
} from 'shared/context/GlobalState';
import CommonModelSelectionModal from '../Common/CommonModelSelectionModal';
import StyledTextArea from '../StyledTextArea';
import {
    RagModal
} from '../Common/RAG/RagModal';
import {
    useParams
} from 'react-router-dom';
import {
    checkRequiredFields
} from '../Instructions/helper';
import {
    Label
} from 'shared/components/ui/label';
import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    ResizablePanelGroup, ResizablePanel, ResizableHandle
} from 'shared/components/ui/resizable';
import {
    AiLabContext
} from 'builders/builderInternal/context/AiLabContext';
import {
    Badge
} from 'shared/components/ui/badge';
import SaveOrUpdateInstruction from './SaveOrUpdateInstruction';
import {
    ImageSelectionModal
} from '../Common/File/ImageSelectionModal';
import {
    ImagePreviewCard
} from '../Common/File/ImagePreviewCard';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    apiKeyService
} from 'shared/api';
import {
    normalizePredictionOutput
} from '../Common/predictionUtils';
import labHelper from 'builders/builderInternal/classes/AILabHelper';
import {
    inferCategory
} from 'builders/builderInternal/classes/modelCategories';

export default function LivePromptInstructionForm({
    handleReloadModels, modelProviders, form, setForm, loadedInstruction = null, setLoadedInstruction = null
}) {

    const [output, setOutput] = useState('');
    const [btnLoading, setBtnLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [isContextModalOpen, setIsContextModalOpen] = useState(false);
    const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isImageSelectionModalOpen, setIsImageSelectionModalOpen] = useState(false);
    const [isInputDataModalOpen, setIsInputDataModalOpen] = useState(false);
    const [apiKeys, setApiKeys] = useState([]);

    const {
        oragID, subOragID
    } = useParams();

    // const openContextModal = () => setIsContextModalOpen(true);
    const closeContextModal = () => setIsContextModalOpen(false);
    const openInputDataModal = () => setIsInputDataModalOpen(true);
    const closeInputDataModal = () => setIsInputDataModalOpen(false);
    const openImageSelectionModal = () => setIsImageSelectionModalOpen(true);
    const closeImageSelectionModal = () => setIsImageSelectionModalOpen(false);
    const openSaveModal = () => setIsSaveModalOpen(true);
    const closeSaveModal = () => setIsSaveModalOpen(false);
    const openUpdateModal = () => setIsUpdateModalOpen(true);
    const closeUpdateModal = () => setIsUpdateModalOpen(false);

    const {
        addNewNotifcation, user
    } = useContext(Context);
    const {
        selectedOrganization
    } = useContext(OrganizationContext);
    const {
        state, dispatch, instructions
    } = useContext(AiLabContext);

    const handleParameter = (data) => setProviderParametersField(form?.model_type, data, setForm);

    const currentModelParameters = useMemo(() => {
        switch (form?.model_type) {
            case 'IBM':
                return form?.watsonParameters || {};
            case 'SambaNova':
                return form?.sambaNovaParameters || {};
            case 'OnPrem':
                return form?.onPremParameters || {};
            case 'Cerebras':
                return form?.cerebrasParameters || {};
            case 'Gemini':
                return form?.geminiParameters || {};
            case 'Mistral':
                return form?.mistralParameters || {};
            case 'Novita':
                return form?.novitaParameters || {};
            case 'Groq':
            default:
                return form?.groqParameters || {};
        }
    }, [
        form?.model_type,
        form?.watsonParameters,
        form?.groqParameters,
        form?.sambaNovaParameters,
        form?.onPremParameters,
        form?.cerebrasParameters,
        form?.geminiParameters,
        form?.mistralParameters,
        form?.novitaParameters
    ]);

    const hasUnsavedChanges = useMemo(() => {
        if (!loadedInstruction) return false;
        const trimmedInstruction = form?.instruction?.trim() || '';
        const loadedInstructionPrompt = loadedInstruction?.prompt?.trim() || '';
        const sameInstruction = trimmedInstruction === loadedInstructionPrompt;
        const sameModelId = (form?.selectedModel || '') === (loadedInstruction?.modelId || '');
        const sameModelType = (form?.model_type || '') === (loadedInstruction?.model_type || '');
        const sameRegion = (form?.region || 'jp-tok') === (loadedInstruction?.region || 'jp-tok');
        const sameParams = JSON.stringify(currentModelParameters) === JSON.stringify(loadedInstruction?.parameters || {});

        return !(sameInstruction && sameModelId && sameModelType && sameRegion && sameParams);
    }, [
        loadedInstruction,
        form?.instruction,
        form?.selectedModel,
        form?.model_type,
        form?.region,
        currentModelParameters
    ]);

    // Helper function to check if instruction has actual content (not just placeholders)
    const hasInstructionContent = useMemo(() => {
        const instructionText = form?.instruction?.trim() || '';
        if (!instructionText) {
            return false;
        }

        // Parse structured prompt to check if any section has content
        const parsed = labHelper.ParseStructuredPrompt(instructionText);
        const hasContent = Object.values(parsed).some(section => {
            const trimmed = section?.trim() || '';
            // Check if it's not empty and not a placeholder pattern
            return trimmed.length > 0 && !/^\[.*\]$/.test(trimmed) && !/^\(.*\)$/.test(trimmed);
        });

        return hasContent;
    }, [form?.instruction]);

    // Check if form is valid for generating output
    const isFormValid = useMemo(() => {
        // Must have a model selected
        if (!form?.selectedModel) {
            return false;
        }

        // Check if instruction has any actual content
        if (hasInstructionContent) {
            return true;
        }

        // Check if inputData has content
        const inputDataText = form?.inputData?.trim() || '';
        if (inputDataText && inputDataText.length > 0) {
            return true;
        }

        return false;
    }, [form?.selectedModel, hasInstructionContent, form?.inputData]);

    // Check if form is valid for saving/updating instruction
    const isSaveFormValid = useMemo(() => {
        // Must have model type
        if (!form?.model_type) {
            return false;
        }

        // Must have a model selected
        if (!form?.selectedModel) {
            return false;
        }

        // Must have instruction content
        if (!hasInstructionContent) {
            return false;
        }

        return true;
    }, [form?.model_type, form?.selectedModel, hasInstructionContent]);

    const selectedModelDetails = useMemo(() => {
        if (!form?.selectedModel) return null;
        const exactMatch = modelProviders.find(model =>
            model.model_id === form.selectedModel &&
            (!form?.model_type || model.provider_type === form.model_type) &&
            (!form?.region || !model.region || model.region === form.region)
        );
        if (exactMatch) return exactMatch;
        return modelProviders.find(model => model.model_id === form.selectedModel) || null;
    }, [form?.selectedModel, form?.model_type, form?.region, modelProviders]);

    const isVisualModelSelected = useMemo(() => {
        if (!selectedModelDetails) return false;
        return inferCategory(selectedModelDetails) === 'visual';
    }, [selectedModelDetails]);

    const selectFileDisabled = !isVisualModelSelected;
    const selectFileTooltip = selectFileDisabled ? 'Only Visual models support images' : undefined;

    const renderProviderConfiguration = () => {
        if (!form?.model_type) return null;
        const configEntry = providerConfigRegistry[form.model_type];
        if (!configEntry) return null;
        const ConfigComponent = configEntry.component;
        const props = configEntry.getProps({
            form,
            handleParameter
        });
        return <ConfigComponent {...props} />;
    };

    useEffect(() => {
        // check for organization api keys
        if (selectedOrganization.byoKeys && selectedOrganization.byoKeys.length > 0) {
            const ibmKeys = selectedOrganization.byoKeys.find(item => item.apiType === 'IBM');
            if (ibmKeys) {
                setApiKeys(prev => ([...prev, {
                    ...ibmKeys,
                    name: ibmKeys.name,
                    value: ibmKeys._id
                }]));
            }
        }
        // check for user api keys
        const fetchApiKeys = async () => {
            try {
                const res = await apiKeyService.listApiKeys('user', user._id);
                const ibmKeys = res.data.find(item => item.apiType === 'IBM');
                if (ibmKeys) {
                    setApiKeys(prev => ([...prev, {
                        ...ibmKeys,
                        name: ibmKeys.name,
                        value: ibmKeys._id
                    }]));
                }
            } catch (error) { /* empty */

            }
        };

        fetchApiKeys();

    }, []);

    useEffect(() => {
        if (loadedInstruction && loadedInstruction.level_type && loadedInstruction.api_key_id) {
            setForm('level_type', loadedInstruction.level_type);
            setForm('api_key_id', loadedInstruction.api_key_id);
        } else {
            if (apiKeys.length > 0) {
                const ibmKeys = apiKeys.find(item => item.levelType === 'organization');
                if (ibmKeys) {
                    setForm('level_type', ibmKeys.levelType);
                    setForm('api_key_id', ibmKeys._id);
                } else {
                    const userKey = apiKeys.find(item => item.levelType === 'user');
                    if (userKey) {
                        setForm('level_type', userKey.levelType);
                        setForm('api_key_id', userKey._id);
                    }
                }
            } else if (form?.model_type === 'IBM' && form?.selectedModel && !form?.api_key_id) {
                // When no API keys are available, set default API key
                setForm('level_type', 'default');
                setForm('api_key_id', 'default');
            }
        }

    }, [apiKeys, loadedInstruction, form?.model_type, form?.selectedModel]);

    // hydrate form when a saved instruction is loaded
    useEffect(() => {
        if (!loadedInstruction) return;

        // basic fields
        setForm('model_type', loadedInstruction.model_type);
        setForm('selectedModel', loadedInstruction.modelId);

        // Load the entire prompt as instruction
        // Check if the prompt is already in structured format or needs to be converted
        let instructionText = loadedInstruction.prompt || '';

        // If the prompt doesn't have section markers, it might be from the old Anatomy component
        // Check if it already has ## markers (structured format)
        const hasStructuredFormat = instructionText.includes('## Instruction') ||
                                     instructionText.includes('## Context') ||
                                     instructionText.includes('## Target Format');

        if (!hasStructuredFormat && instructionText.trim()) {
            // Legacy instruction - keep as is (backward compatible)
            // The user can manually add sections if they want
            setForm('instruction', instructionText);
        } else {
            // Already structured or empty
            setForm('instruction', instructionText);
        }

        // parameters by provider
        if (loadedInstruction.model_type === 'IBM') {
            setForm('watsonParameters', loadedInstruction.parameters || {});
        } else if (loadedInstruction.model_type === 'Groq') {
            setForm('groqParameters', loadedInstruction.parameters || {});
        } else if (loadedInstruction.model_type === 'SambaNova') {
            setForm('sambaNovaParameters', loadedInstruction.parameters || {});
        } else if (loadedInstruction.model_type === 'Cerebras') {
            setForm('cerebrasParameters', loadedInstruction.parameters || {});
        } else if (loadedInstruction.model_type === 'OnPrem') {
            setForm('onPremParameters', loadedInstruction.parameters || {});
        } else if (loadedInstruction.model_type === 'Gemini') {
            setForm('geminiParameters', loadedInstruction.parameters || {});
        } else if (loadedInstruction.model_type === 'Mistral') {
            setForm('mistralParameters', loadedInstruction.parameters || {});
        } else if (loadedInstruction.model_type === 'Novita') {
            setForm('novitaParameters', loadedInstruction.parameters || {});
            // hydrate credentials if present on the instruction
            if (loadedInstruction.api_key || loadedInstruction.base_url) {
                setForm('fullModelData', {
                    api_key: loadedInstruction.api_key || null,
                    base_url: loadedInstruction.base_url || null
                });
            } else {
                // fallback: try to obtain credentials from modelProviders by model_id/provider_type/region
                const match =
                    modelProviders.find(model =>
                        model.model_id === loadedInstruction.modelId &&
                        model.provider_type === 'OnPrem' &&
                        (loadedInstruction.region ? model.region === loadedInstruction.region : true)
                    ) ||
                    modelProviders.find(model =>
                        model.model_id === loadedInstruction.modelId &&
                        model.provider_type === 'OnPrem'
                    );
                if (match) {
                    setForm('fullModelData', {
                        api_key: match.api_key || match.apiKey || null,
                        base_url: match.base_url || match.baseUrl || match.url || null
                    });
                    if (match.region) setForm('region', match.region);
                }
            }
        }
    }, [loadedInstruction, modelProviders]);

    const selectedModel = (data) => {
        setForm('selectedModel', data.modelName);
        setForm('model_type', data.modelType);
        setForm('region', data.modelRegion);

        // Store the full model data for OnPrem models to access api_key and base_url
        if (data.modelType === 'OnPrem') {
            const fullModelData =
                modelProviders.find(model =>
                    model.model_id === data.modelName &&
                    model.provider_type === data.modelType &&
                    model.region === data.modelRegion
                ) ||
                modelProviders.find(model =>
                    model.model_id === data.modelName &&
                    model.provider_type === data.modelType
                );
            if (fullModelData) {
                setForm('fullModelData', fullModelData);
            }
        } else {
            // Clear any stale full model data when switching away from OnPrem
            setForm('fullModelData', null);
        }
    };

    const handleCopyImages = (fileList) => {
        // Filter out folders - only process actual files with presigned_url
        const actualFiles = fileList.filter(item => item.type === 'file' && item.presigned_url);

        const formattedImages = actualFiles.map(item => ({
            url: item.presigned_url,
            name: item.name
        }));

        setForm('selectedImages', formattedImages);

        actualFiles.forEach(item => {
            addNewNotifcation(`${item.name} added.`, 'success');
        });
    };

    const handleCopyImageUrl = (resultUrl, resultName) => {
        setForm('selectedImages', [{
            url: resultUrl,
            name: resultName
        }]);
        addNewNotifcation(`${resultName} added.`, 'success');
    };

    const removeURL = (index) => {
        const updatedImageList = form?.selectedImages.filter((_, i) => i !== index);
        setForm('selectedImages', updatedImageList);
    };

    const handleSendPrompt = async () => {
        if (!form?.selectedModel) {
            return addNewNotifcation('Please select a foundation model.', 'warning');
        } else if (!form?.instruction.trim()) {
            return addNewNotifcation('Please enter an instruction.', 'warning');
        }

        try {
            setBtnLoading(true);
            setOutput('');

            let params = form?.watsonParameters;
            if (form?.model_type === 'IBM') {
                if (form?.watsonParameters.mode === 'greedy') {
                    params = Object.fromEntries(
                        Object.entries(form?.watsonParameters).filter(([key]) => key === 'max_new_tokens' || key === 'mode' || key === 'stop_sequences')
                    );
                }
            }

            const finalParams = {
                IBM: params,
                Groq: form?.groqParameters,
                SambaNova: form?.sambaNovaParameters,
                OnPrem: form?.onPremParameters,
                Gemini: form?.geminiParameters,
                Cerebras: form?.cerebrasParameters,
                Mistral: form?.mistralParameters,
                Novita: form?.novitaParameters
            };

            const trimmedInstruction = form?.instruction?.trim() || '';
            const trimmedInputData = form?.inputData?.trim() || '';
            const currentModelParams = finalParams[form?.model_type] || {};

            const shouldUsePredictEndpoint = Boolean(loadedInstruction?.unique_name) && !hasUnsavedChanges;

            if (shouldUsePredictEndpoint) {
                const body = {
                    org_id: loadedInstruction.org_id,
                    sub_org_id: loadedInstruction.sub_org_id,
                    unique_name: loadedInstruction?.unique_name,
                    query: trimmedInputData,
                    image_urls: form?.selectedImages.map(images => images.url) || [],
                };

                if (form?.model_type === 'OnPrem') {
                    const modelInfo = form?.fullModelData || {};
                    const apiKey = modelInfo.api_key || modelInfo.apiKey;
                    const baseUrl = modelInfo.base_url || modelInfo.baseUrl || modelInfo.url;
                    if (!apiKey || !baseUrl) {
                        addNewNotifcation('Missing OnPrem credentials. Please ensure api_key and base_url are available for the selected model.', 'warning');
                        setBtnLoading(false);
                        return;
                    }
                    body.api_key = apiKey;
                    body.base_url = baseUrl;
                }

                const res = await unifiedModelWSService.predict(body, form?.model_type);
                setOutput(normalizePredictionOutput(res.result.prediction));
            } else {
                const body = {
                    modelId: form?.selectedModel,
                    prompt: trimmedInstruction,
                    parameters: currentModelParams,
                    query: trimmedInputData,
                    region: form?.region || 'jp-tok',
                    image_urls: form?.selectedImages.map(images => images.url) || [],
                };

                // Add api_key and base_url for OnPrem models and validate
                if (form?.model_type === 'OnPrem') {
                    const modelInfo = form?.fullModelData || {};
                    const apiKey = modelInfo.api_key || modelInfo.apiKey;
                    const baseUrl = modelInfo.base_url || modelInfo.baseUrl || modelInfo.url;
                    if (!apiKey || !baseUrl) {
                        addNewNotifcation('Missing OnPrem credentials. Please ensure api_key and base_url are available for the selected model.', 'warning');
                        setBtnLoading(false);
                        return;
                    }
                    body.api_key = apiKey;
                    body.base_url = baseUrl;
                }

                const res = await unifiedModelWSService.experiment(body, form?.model_type, oragID, subOragID);
                setOutput(normalizePredictionOutput(res.result.prediction));
            }
        } catch (error) {
            if (typeof error === 'string' && error.includes('rate_limit_exceeded')) {
                addNewNotifcation('Model rate limit exceeded.', 'warning');
            } else if (typeof error === 'object' && error?.websocket_error) {
                addNewNotifcation('Error generating response.', 'danger');
            } else {
                addNewNotifcation('Error generating response.', 'danger');
            }

        } finally {
            setBtnLoading(false);
        }
    };

    // Save
    const handleCreateInstruction = async (unique_name, modelDescription, isUpdate = false) => {
        // Validate instruction content first
        if (!hasInstructionContent) {
            return addNewNotifcation(
                'Instruction must contain a prompt before it can be saved or updated.',
                'warning'
            );
        }

        const requiredFields = [
            {
                field: 'model_type',
                name: 'model type'
            },
            {
                field: 'unique_name',
                name: 'unique name'
            },
            {
                field: 'modelId',
                name: 'model'
            },
        ];

        const instructionObj = {
            model_type: isUpdate ? loadedInstruction?.model_type : form?.model_type,
            unique_name,
            modelId: form?.selectedModel,
            prompt: form?.instruction.trim(),
            parameters: form?.model_type === 'IBM' ? form?.watsonParameters
                : form?.model_type === 'SambaNova' ? form?.sambaNovaParameters
                    : form?.model_type === 'OnPrem' ? form?.onPremParameters
                        : form?.model_type === 'Cerebras' ? form?.cerebrasParameters
                            : form?.model_type === 'Gemini' ? form?.geminiParameters
                                : form?.model_type === 'Mistral' ? form?.mistralParameters
                                    : form?.model_type === 'Novita' ? form?.novitaParameters
                                        : form?.groqParameters,
            org_id: oragID,
            sub_org_id: subOragID,
            region: form?.region || 'jp-tok',
            modelDescription,
            user_id: user._id,
            metadata: {},
        };

        // add ibm api connectors
        if (instructionObj.model_type === 'IBM') {
            if (form.api_key_id && form.level_type) {
                instructionObj['api_key_id'] = form.api_key_id;
                instructionObj['level_type'] = form.level_type;
            }
        }

        if (isUpdate) {
            instructionObj.instructionId = loadedInstruction?._id;
            if (loadedInstruction?.model_type !== form?.model_type) {
                instructionObj['new_model_type'] = form?.model_type;
                instructionObj['creation_timestamp'] = loadedInstruction.creation_timestamp;
            }
        }

        const checkFields = checkRequiredFields(instructionObj, requiredFields);
        if (!checkFields.isValid) {
            return addNewNotifcation(`Please fill in all required fields: ${checkFields.missingFields.map(item => item.name).join(', ')}`, 'warning');
        }

        // check is model is deprecated or not
        const modelProvider = modelProviders.find(model => model.model_id === instructionObj.modelId);
        if (!modelProvider) {
            return addNewNotifcation(`Model "${instructionObj.modelId}" is deprecated. Please select another model.`, 'warning');
        }

        try {
            isUpdate ? setUpdateLoading(true) : setSaveLoading(true);

            const res = isUpdate
                ? await instructionService.updateModel(instructionObj)
                : await instructionService.createModel(instructionObj);

            addNewNotifcation(`Instruction ${isUpdate ? 'updated' : 'created'} successfully`, 'success');
            setLoadedInstruction(res?.data?.unique_name ? res.data : loadedInstruction);
            //
            if (!isUpdate) {
                dispatch({
                    type: 'SET_INSTRUCTIONS',
                    field: 'savedInstructions',
                    value: [...instructions, res.data]
                });
            } else {
                dispatch({
                    type: 'SET_INSTRUCTIONS',
                    field: 'savedInstructions',
                    value: instructions.map((item) => item.unique_name === unique_name ? res.data : item)
                });
            }
        } catch (error) {

            const message = error.response?.status === 409
                ? `Instruction with ${unique_name} already exists`
                : 'Something went wrong. Please try again';
            addNewNotifcation(message, 'danger');
        } finally {
            if (isUpdate) {
                setUpdateLoading(false);
                closeUpdateModal();
            } else {
                setSaveLoading(false);
                closeSaveModal();
            }
        }
    };

    // anatomy action buttons
    const actions = {
        instruction: [
            {
                icon: <WandSparkles size={18}/>,
                onClick: () => {alert('clicked');},
                disabled: true,
                visible: false
            }
        ],
        context: [
            {
                icon: <Globe size={18}/>,
                onClick: () => {alert('clicked');},
                disabled: true,
                visible: false
            },
            // {icon: <span className=''>RAG</span>, onClick: openContextModal, disabled: false, visible: true}
        ],
        inputData: [
            {
                icon: <ArrowUpFromLine size={18}/>,
                onClick: () => {alert('clicked');},
                disabled: true,
                visible: false
            },
            {
                icon: <span className=''>RAG</span>,
                onClick: openInputDataModal,
                disabled: false,
                visible: true
            },
            {
                icon: <span className=''>Select Image</span>,
                onClick: openImageSelectionModal,
                disabled: selectFileDisabled,
                visible: true,
                tooltip: selectFileTooltip
            }
        ]
    };

    const handleSelectApiKey = (value) => {
        if (value === 'default') {
            setForm('level_type', 'default');
            setForm('api_key_id', 'default');
        } else {
            const ibmKeys = apiKeys.find(item => item.value === value);
            if (ibmKeys) {
                setForm('level_type', ibmKeys.levelType);
                setForm('api_key_id', ibmKeys._id);
            }
        }
    };

    return (
        <div className="flex flex-row gap-4 w-full h-full">
            <ResizablePanelGroup direction="horizontal" className="w-full gap-2 h-full">
                <ResizablePanel defaultSize={70} minSize={30} className='flex flex-col gap-4 w-full overflow-y-auto'>
                    <div className="border border-solid bg-card text-card-foreground shadow rounded-md px-4 py-6 h-fit">
                        <div className='flex gap-2'>
                            <div className="w-full">
                                <CommonModelSelectionModal
                                    handleReloadModels={handleReloadModels}
                                    modelProviders={modelProviders}
                                    finalModel={{
                                        modelName: form?.selectedModel,
                                        modelType: form?.model_type,
                                        modelRegion: form?.region
                                    }}
                                    onModelSelect={selectedModel}
                                />
                            </div>

                            {renderProviderConfiguration()}

                        </div>

                        {form.model_type === 'IBM' && form.selectedModel && (
                            <div className='my-3'>
                                <Label className='text-right mt-2'> API connection </Label>
                                <Select value={form.api_key_id || 'default'} onValueChange={handleSelectApiKey} id="apikey" className="w-full">
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select apikey" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {apiKeys.length > 0 ? (
                                            apiKeys.map((item) => (
                                                <SelectItem key={item.name} value={item.value}>{item.name} <small className='text-muted-foreground text-sm'> {`(${item.levelType} key)`} </small> </SelectItem>
                                            ))
                                        ) : (
                                            <SelectItem value="default">Default API Key</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {loadedInstruction && hasUnsavedChanges && (
                            <div className="my-3 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                                <AlertCircle className="h-4 w-4" />
                                <span>You have unsaved changes.</span>
                            </div>
                        )}

                        <div className="flex flex-row gap-2 mt-2 items-center">
                            <Label>Instruction: </Label>
                            <Badge className="">{loadedInstruction?.unique_name || 'Unsaved'}</Badge>
                        </div>

                        <Separator className='my-3' />

                        <AnatomylivePrompt isExecute={false} actions={actions}/>

                        <div className="flex flex-row justify-end gap-2 mt-2 px-1">
                            { loadedInstruction && (
                                <Button
                                    variant="outline"
                                    onClick={openUpdateModal}
                                    disabled={!isSaveFormValid}
                                >
                                    Update instruction
                                </Button>
                            )}
                            <Button
                                onClick={openSaveModal}
                                disabled={!isSaveFormValid}
                            >
                                {loadedInstruction?.unique_name ? 'Save As' : 'Save instruction'}
                            </Button>
                        </div>
                    </div>

                    <div className="border border-solid bg-card text-card-foreground shadow rounded-md px-4 py-6 h-fit">
                        <div className='flex flex-col gap-4 w-full p-1'>
                            <StyledTextArea
                                value={state.promptLab.form.inputData}
                                onChange={(e) => setForm('inputData', e.target.value)}
                                labelText="Input Data"
                                tooltipText="Include any data for reference, or any image file for the image models"
                                required={false}
                                rows={2}
                                actions={actions?.inputData || []}
                            />
                        </div>

                        <div>
                            {form?.selectedImages && form?.selectedImages.length > 0 && (
                                <div className="flex flex-wrap gap-3 p-2">
                                    {form?.selectedImages?.map((item, index) => (
                                        <ImagePreviewCard
                                            key={index}
                                            item={item}
                                            index={index}
                                            onRemove={removeURL}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                    </div>
                    {/* RAG modal */}
                    <RagModal isOpen={isContextModalOpen} onClose={closeContextModal} form={state.promptLab} setForm={setForm} field={'context'}/>
                    <RagModal
                        isOpen={isInputDataModalOpen}
                        onClose={closeInputDataModal}
                        form={state.promptLab}
                        setForm={setForm}
                        field={'inputData'}
                        handleCopyImageUrl={handleCopyImageUrl}
                    />
                    {/* Save/Update modal */}
                    <SaveOrUpdateInstruction
                        isOpen={isSaveModalOpen || isUpdateModalOpen}
                        onClose={isSaveModalOpen ? closeSaveModal : closeUpdateModal}
                        onSaveOrUpdate={handleCreateInstruction}
                        loading={isSaveModalOpen ? saveLoading : updateLoading}
                        loadedInstruction={loadedInstruction}
                        isUpdate={isUpdateModalOpen}
                        isFormValid={isSaveFormValid}
                        onValidationError={() => {
                            addNewNotifcation(
                                'Prompt cannot be empty. Please provide instruction content before saving.',
                                'warning'
                            );
                        }}
                    />
                    <ImageSelectionModal
                        isOpen={isImageSelectionModalOpen}
                        onClose={closeImageSelectionModal}
                        handleImageSelect={handleCopyImages}
                    />
                </ResizablePanel>
                <ResizableHandle />
                <ResizablePanel defaultSize={30} minSize={30} className="w-full overflow-y-auto">
                    <Output output={output} handleSendPrompt={handleSendPrompt} btnLoading={btnLoading} isFormValid={isFormValid}/>
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    );
}